import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Clock, AlertCircle, Trash2 } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';

export default function ArticleContentModal({ article, onClose, userRole, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleContent, setArticleContent] = useState(null);
  const fetchingRef = useRef(false);

  useEffect(() => {
    // Reset fetching ref when article changes
    fetchingRef.current = false;

    // Open Google News in popup instead of modal
    if (article?.url?.includes("news.google")) {
      window.open(article.url, "_blank", "width=1200,height=900,scrollbars=yes,resizable=yes");
      onClose();
      return;
    }

    if (!article?.url) {
      setError('No article URL provided');
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches within the same render
    if (fetchingRef.current) return;

    fetchArticleContent();
  }, [article]);

  const fetchArticleContent = async () => {
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    console.log('📰 Article data received:', {
      hasScrapedFullText: !!article.scrapedFullText,
      hasScrapedSummary: !!article.scrapedSummary,
      hasSummaryEn: !!article.summaryEn,
      hasDescription: !!article.description,
      title: article.title
    });

    // Check if content was cleared (needs re-parsing)
    const hasContent = article.scrapedFullText || article.summaryEn || article.scrapedSummary;

    if (!hasContent) {
      console.log('🔄 No saved content found, triggering on-demand parsing...');
      try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${API_URL}/api/article-reader?url=${encodeURIComponent(article.url)}`);
        const data = await response.json();
        
        if (data.success) {
          console.log('✅ Article parsed successfully');
          
          // Save to Firestore
          await fetch(`${API_URL}/api/news/${article.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scrapedFullText: data.textContent,
              parsedAt: new Date().toISOString()
            })
          });
          
          setArticleContent({
            success: true,
            title: data.title || article.titleKr || article.title,
            textContent: data.textContent,
            excerpt: data.excerpt || article.summaryKr || article.summaryEn,
            siteName: data.siteName || article.source,
            publishedTime: data.publishedTime || article.publishedAt,
            fromParsing: true
          });
        } else {
          throw new Error(data.error || 'Failed to parse article');
        }
      } catch (error) {
        console.error('Error parsing article:', error);
        setError('Failed to parse article content');
      }
      setLoading(false);
      fetchingRef.current = false;
      return;
    }

    // Use existing database content
    const content = article.scrapedFullText || article.summaryEn || article.scrapedSummary || article.description || article.title;

    console.log('✅ Using database content, length:', content.length);

    setArticleContent({
      success: true,
      title: article.titleKr || article.title,
      textContent: content,
      excerpt: article.summaryKr || article.summaryEn,
      siteName: article.source,
      publishedTime: article.publishedAt,
      fromDatabase: true
    });

    setLoading(false);
    fetchingRef.current = false;
  };

  if (!article) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-xl font-bold text-gray-900 leading-tight mb-3">
              {articleContent?.title || article.titleKr || article.title}
            </h2>
            <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
              {article.ticker && (
                <>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-semibold">
                    {article.ticker}
                  </span>
                  <span className="text-gray-300">•</span>
                </>
              )}
              {articleContent?.siteName && (
                <>
                  <span className="font-medium">{articleContent.siteName}</span>
                  <span className="text-gray-300">•</span>
                </>
              )}
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>
                  {new Date(article.publishedAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              {articleContent?.byline && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-gray-700">{articleContent.byline}</span>
                </>
              )}
              {articleContent?.fromDatabase && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                    Saved Content
                  </span>
                </>
              )}
            </div>
          </div>
          {userRole === USER_ROLES.ADMIN && (
            <button
              onClick={() => {
                if (window.confirm("Clear saved content? Article will need to be re-parsed.")) {
                  onDelete?.(article);
                  onClose();
                }
              }}
              className="flex-shrink-0 p-2 hover:bg-red-100 rounded-full transition-colors mr-2"
              title="Clear saved content"
            >
              <Trash2 className="w-5 h-5 text-red-600" />
            </button>
          )}
                    <button
            onClick={onClose}
            className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading article content...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-2">
                    Unable to Load Article
                  </h3>
                  <p className="text-red-800 mb-4">{error}</p>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    View Original Article
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          ) : articleContent ? (
            <div className="prose prose-lg max-w-none">
              {/* Article Excerpt */}
              {articleContent.excerpt && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 italic text-gray-700">
                  {articleContent.excerpt}
                </div>
              )}

              {/* Article Content */}
              <div
                className="article-content text-gray-800 leading-relaxed"
                style={{
                  fontSize: '16px',
                  lineHeight: '1.8'
                }}
              >
                {articleContent.textContent ? (
                  // Use plain text content (no ads, clean format)
                  <div className="whitespace-pre-wrap">{articleContent.textContent}</div>
                ) : articleContent.content ? (
                  // Fallback to HTML content
                  <div dangerouslySetInnerHTML={{ __html: articleContent.content }} />
                ) : (
                  <p>No content available</p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t border-gray-200 flex justify-between items-center gap-3 bg-gray-50">
          <div className="flex gap-3 flex-1">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              원본 웹사이트 열기
            </a>
            <div className="text-xs text-gray-500 flex items-center">
              ℹ️ 광고 및 페이월이 포함될 수 있습니다
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
